import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiPlus, FiFolder, FiUsers, FiFile, FiLock, FiShield, FiRefreshCw } from 'react-icons/fi';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import { getUserDataRooms, check2FAStatus, DataRoom } from '../services/dataRoomService';

const DataRooms: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isAdmin } = useSubscription();
  const [dataRooms, setDataRooms] = useState<DataRoom[]>([]);
  const [loading, setLoading] = useState(true);
  const [has2FA, setHas2FA] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadDataRooms();
    check2FAEnabled();
  }, []);

  const loadDataRooms = async () => {
    try {
      setLoading(true);
      const rooms = await getUserDataRooms();
      setDataRooms(rooms);
    } catch (err) {
      console.error('Error loading data rooms:', err);
      setError('Failed to load data rooms');
    } finally {
      setLoading(false);
    }
  };

  const check2FAEnabled = async () => {
    const enabled = await check2FAStatus();
    setHas2FA(enabled);
  };

  const handleCreateRoom = () => {
    if (!has2FA) {
      navigate('/data-rooms/setup-2fa');
      return;
    }
    navigate('/data-rooms/create');
  };

  const getStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      draft: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      active: 'bg-green-500/20 text-green-400 border-green-500/30',
      archived: 'bg-gray-500/20 text-gray-400 border-gray-500/30'
    };
    return styles[status] || styles.draft;
  };

  const getRoleBadge = (role: string) => {
    const styles: Record<string, { bg: string; text: string }> = {
      global_admin: { bg: 'bg-red-500/20', text: 'text-red-400' },
      owner: { bg: 'bg-purple-500/20', text: 'text-purple-400' },
      data_room_admin: { bg: 'bg-cyan-500/20', text: 'text-cyan-400' },
      uploader: { bg: 'bg-blue-500/20', text: 'text-blue-400' },
      viewer: { bg: 'bg-gray-500/20', text: 'text-gray-400' }
    };
    const style = styles[role] || styles.viewer;
    return `${style.bg} ${style.text}`;
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <p className="text-gray-400">Please log in to access Data Room</p>
      </div>
    );
  }

  // Require 2FA for ALL users to access Data Room
  if (!loading && !has2FA) {
    return (
      <div className="min-h-screen bg-gray-950 pt-20 pb-12 px-4">
        <div className="max-w-lg mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-gray-900/50 rounded-2xl border border-purple-500/30 p-8 text-center"
          >
            <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-purple-600/20 to-cyan-600/20 border border-purple-500/30 flex items-center justify-center">
              <FiShield className="w-10 h-10 text-cyan-400" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-3">
              Two-Factor Authentication Required
            </h2>
            <p className="text-gray-400 mb-6">
              For security purposes, all users must enable Google Authenticator 2FA to access the Data Room.
            </p>
            <button
              onClick={() => navigate('/data-rooms/setup-2fa')}
              className="px-6 py-3 rounded-xl bg-gray-900/80 border border-cyan-500/20 hover:border-cyan-500/40 text-gray-300 hover:text-white font-semibold transition-all"
            >
              Set Up 2FA Now
            </button>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-950 pt-20 pb-12 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-4"
        >
          <div>
            <h1 className="text-3xl font-bold text-white flex items-center gap-3">
              <FiFolder className="text-cyan-400" />
              Investor Data Room
            </h1>
            <p className="text-gray-400 mt-2">
              Secure document sharing for institutional investors
            </p>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={loadDataRooms}
              disabled={loading}
              className="p-3 rounded-xl bg-gray-900/80 border border-purple-500/20 hover:border-purple-500/40 text-gray-300 hover:text-white transition-all"
            >
              <FiRefreshCw className={loading ? 'animate-spin' : ''} />
            </button>
            
            {/* Only admins can create data rooms */}
            {isAdmin && (
              <button
                onClick={handleCreateRoom}
                className="flex items-center gap-2 px-5 py-3 rounded-xl bg-gray-900/80 border border-cyan-500/20 hover:border-cyan-500/40 text-gray-300 hover:text-white font-medium transition-all"
              >
                <FiPlus className="w-5 h-5 text-cyan-400" />
                Create Data Room
              </button>
            )}
          </div>
        </motion.div>

        {/* 2FA Warning - only show to admins since only they can create data rooms */}
        {isAdmin && !has2FA && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 p-4 rounded-xl bg-amber-500/10 border border-amber-500/30 flex items-start gap-3"
          >
            <FiShield className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-amber-400 font-medium">2FA Required for Data Room Management</p>
              <p className="text-amber-400/70 text-sm mt-1">
                To create or manage data rooms, you must enable Two-Factor Authentication (Google Authenticator).
              </p>
              <button
                onClick={() => navigate('/data-rooms/setup-2fa')}
                className="mt-2 text-sm text-amber-400 hover:text-amber-300 underline"
              >
                Enable 2FA Now →
              </button>
            </div>
          </motion.div>
        )}

        {/* Loading */}
        {loading && (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin" />
          </div>
        )}

        {/* Error */}
        {error && !loading && (
          <div className="text-center py-20">
            <p className="text-red-400">{error}</p>
            <button
              onClick={loadDataRooms}
              className="mt-4 text-cyan-400 hover:text-cyan-300"
            >
              Try again
            </button>
          </div>
        )}

        {/* Empty State */}
        {!loading && !error && dataRooms.length === 0 && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center py-20"
          >
            <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-purple-600/20 to-cyan-600/20 border border-purple-500/30 flex items-center justify-center">
              <FiLock className="w-10 h-10 text-cyan-400" />
            </div>
            <h2 className="text-xl font-semibold text-white mb-2">No Data Room Yet</h2>
            <p className="text-gray-400 max-w-md mx-auto mb-6">
              {isAdmin 
                ? 'Create your first secure data room to share documents with institutional investors.'
                : 'You are not a member of any data rooms yet. Contact an administrator to get access.'}
            </p>
            {isAdmin && (
              <button
                onClick={handleCreateRoom}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-gray-900/80 border border-cyan-500/20 hover:border-cyan-500/40 text-gray-300 hover:text-white font-medium transition-all"
              >
                <FiPlus className="w-5 h-5 text-cyan-400" />
                Create Your First Data Room
              </button>
            )}
          </motion.div>
        )}

        {/* Data Rooms Grid */}
        {!loading && !error && dataRooms.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {dataRooms.map((room, index) => (
              <motion.div
                key={room.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                onClick={() => navigate(`/data-rooms/${room.id}`)}
                className="p-5 rounded-2xl bg-gray-900/50 border border-gray-800/50 hover:border-purple-500/30 cursor-pointer transition-all group"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-600/20 to-cyan-600/20 border border-purple-500/30 flex items-center justify-center group-hover:border-purple-500/50 transition-all">
                    <FiFolder className="w-6 h-6 text-cyan-400" />
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-1 text-xs rounded-full border ${getStatusBadge(room.status)}`}>
                      {room.status}
                    </span>
                    {room.role && (
                      <span className={`px-2 py-1 text-xs rounded-full ${getRoleBadge(room.role)}`}>
                        {room.role.replace('_', ' ')}
                      </span>
                    )}
                  </div>
                </div>

                <h3 className="text-lg font-semibold text-white mb-1 group-hover:text-cyan-400 transition-colors">
                  {room.name}
                </h3>
                <p className="text-gray-400 text-sm mb-4 line-clamp-2">
                  {room.id === 'd6fae108-2ef9-44fd-a35b-67d438633c90'
                    ? 'Data Room 1 - Jess Davis / Digital Trvst'
                    : room.id === 'd0e07e74-6ed2-4dee-b229-2db32e4ff2dd' 
                      ? 'Data Room 2 - Michael Jacobsen / Aurum Caeli Holdings S.A.'
                      : room.id === '52e30664-efc6-4e13-9e07-805fca6d8fb4'
                        ? 'Data Room 3 - Jeremy Gonske / Bloctech Investments'
                        : room.description || ''}
                </p>

                <div className="flex items-center gap-4 text-sm text-gray-500">
                  <span className="flex items-center gap-1">
                    <FiUsers className="w-4 h-4" />
                    {room.member_count || 0} members
                  </span>
                  <span className="flex items-center gap-1">
                    <FiFile className="w-4 h-4" />
                    {room.file_count || 0} files
                  </span>
                </div>

                {room.owner_email && (
                  <p className="text-xs text-gray-600 mt-3">
                    Owner: {room.owner_email}
                  </p>
                )}
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default DataRooms;
